import React, { useEffect, useState } from "react";
import Lightbox from "../ui/Lightbox";

const MAX_IMAGES = 200; // upper bound; only existing files will be used
const SLIDESHOW_DELAY_MS = 4500;

function Gallery() {
  const [images, setImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let isCancelled = false;
    const found = [];

    const loadImage = (i) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(`/gallery/gallery${i}.jpg`);
        img.onerror = () => resolve(null);
        img.src = `/gallery/gallery${i}.jpg`;
      });

    (async () => {
      const tasks = [];
      for (let i = 1; i <= MAX_IMAGES; i++) {
        tasks.push(loadImage(i));
      }
      const results = await Promise.all(tasks);
      if (isCancelled) return;
      results.forEach((src) => {
        if (src) found.push(src);
      });
      setImages(found);
    })();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => prev % images.length);
  }, [images.length]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxIndex(-1);
    document.body.style.overflow = "";
  };

  const shiftNext = () => {
    if (images.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const shiftPrev = () => {
    if (images.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNext = React.useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const showPrev = React.useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === -1) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, images.length, showNext, showPrev]);

  useEffect(() => {
    if (images.length <= 1 || lightboxIndex !== -1) return undefined;

    const intervalId = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, SLIDESHOW_DELAY_MS);

    return () => window.clearInterval(intervalId);
  }, [images.length, lightboxIndex]);

  const currentImage = images[currentIndex];

  return (
    <div className="container">
      <h2 className="section-title">Club Moments</h2>
      <p className="muted">Snapshots from our sessions, events, and community.</p>

      {images.length > 0 ? (
        <div className="gallery-slideshow" aria-label="Gallery slideshow">
          <div className="gallery-stage">
            <button
              className="gallery-slide-feature"
              onClick={() => openLightbox(currentIndex)}
              aria-label={`Open club moment ${currentIndex + 1}`}
            >
              <img src={currentImage} alt={`Club moment ${currentIndex + 1}`} />
              <span className="gallery-stage-gradient" aria-hidden="true" />
              <span className="gallery-stage-caption">
                <strong>Club moment {currentIndex + 1}</strong>
                <span>Tap to open full view</span>
              </span>
            </button>

            <button className="gallery-arrow gallery-arrow-left" onClick={shiftPrev} aria-label="Previous image">
              &#10094;
            </button>
            <button className="gallery-arrow gallery-arrow-right" onClick={shiftNext} aria-label="Next image">
              &#10095;
            </button>
          </div>

          <div className="gallery-slideshow-footer">
            <div className="gallery-dots" aria-label="Slide navigation">
              {images.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  className={`gallery-dot ${index === currentIndex ? "gallery-dot-active" : ""}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
            <p className="gallery-count">
              {currentIndex + 1} / {images.length}
            </p>
          </div>

          <div className="gallery-thumbnails" aria-label="Gallery thumbnails">
            {images.map((image, index) => (
              <button
                key={`${image}-thumb-${index}`}
                className={`gallery-thumbnail ${index === currentIndex ? "gallery-thumbnail-active" : ""}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Show image ${index + 1}`}
              >
                <img src={image} alt={`Gallery thumbnail ${index + 1}`} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="muted">Gallery coming soon. Add images to /public/gallery/.</p>
      )}

      {lightboxIndex !== -1 && images.length > 0 && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={showPrev}
          onNext={showNext}
        />
      )}
    </div>
  );
}

export default Gallery;
